import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Activity,
  CheckCircle,
  Filter,
  Search,
  ChevronRight,
  AlertCircle,
  XCircle,
  Loader2,
  Server,
  FileWarning,
  Cloud,
  Calendar,
  Shield,
  Database,
  Network,
  Key,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient, CloudIncidentSummary, AWSConnection, OCIConnection } from '../api/client';
import { cn } from '@/lib/utils';
import { getStatusLabel } from '../lib/incidents';
import { format } from 'date-fns';
import awsLogoDark from '../assets/icons/aws-dark.svg';
import awsLogoLight from '../assets/icons/aws.svg';
import OCILogo from '../assets/icons/oci.png';

// Get initials from email address for avatar display
function getInitials(email: string): string {
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// Format timestamp to relative time
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp * 1000;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return 'Just now';
  }
}

// Format timestamp to full datetime
function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Get severity icon and color
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'high':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'medium':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    case 'low':
      return <AlertCircle className="h-4 w-4 text-blue-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}


// Get status icon
function getStatusIcon(status: string) {
  switch (status) {
    case 'open':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'investigating':
      return <Loader2 className="h-4 w-4 text-white animate-spin" />;
    case 'analyzing_code':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'resolved':
      return <FileWarning className="h-4 w-4 text-amber-500" />; // Analysis Complete
    case 'remediation_applied':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'resolved_confirmed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

// Get status badge variant
function getStatusBadgeVariant(status: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (status) {
    case 'open':
      return 'destructive';
    case 'investigating':
      return 'default';
    case 'analyzing_code':
      return 'default';
    case 'resolved':
      return 'secondary'; // Analysis complete - amber/warning style
    case 'remediation_applied':
      return 'default';
    case 'resolved_confirmed':
      return 'outline'; // Final resolved - green/success style
    default:
      return 'secondary';
  }
}

// Get cloud incident type icon
function getCloudIncidentTypeIcon(type: string) {
  if (type.includes('IAM') || type.includes('Access')) {
    return <Key className="h-4 w-4 text-amber-500" />;
  } else if (type.includes('S3') || type.includes('Storage')) {
    return <Database className="h-4 w-4 text-blue-500" />;
  } else if (type.includes('Network') || type.includes('VPC') || type.includes('SecurityGroup')) {
    return <Network className="h-4 w-4 text-purple-500" />;
  } else if (type.includes('Security')) {
    return <Shield className="h-4 w-4 text-red-500" />;
  } else {
    return <Cloud className="h-4 w-4 text-sky-500" />;
  }
}

// Format incident type for display
// Handles patterns like "CLOUDTRAILError" -> "CloudTrail Error", "S3PolicyChange" -> "S3 Policy Change"
function formatIncidentType(type: string): string {
  // Common AWS service abbreviations that should stay uppercase or have special formatting
  const serviceAbbreviations: Record<string, string> = {
    'CLOUDTRAIL': 'CloudTrail',
    'CLOUDWATCH': 'CloudWatch',
    'CLOUDFRONT': 'CloudFront',
    'CLOUDFORMATION': 'CloudFormation',
    'DYNAMODB': 'DynamoDB',
    'ELASTICACHE': 'ElastiCache',
    'ELASTICSEARCH': 'Elasticsearch',
    'OPENSEARCH': 'OpenSearch',
    'APIGATEWAY': 'API Gateway',
    'STEPFUNCTIONS': 'Step Functions',
    'SECRETSMANAGER': 'Secrets Manager',
    'EVENTBRIDGE': 'EventBridge',
    'CODEPIPELINE': 'CodePipeline',
    'CODEBUILD': 'CodeBuild',
    'CODEDEPLOY': 'CodeDeploy',
    'CODECOMMIT': 'CodeCommit',
    'SAGEMAKER': 'SageMaker',
    'GUARDDUTY': 'GuardDuty',
    'SECURITYHUB': 'Security Hub',
    'ACCESSANALYZER': 'Access Analyzer',
    'APPRUNNER': 'App Runner',
    'S3': 'S3',
    'EC2': 'EC2',
    'IAM': 'IAM',
    'VPC': 'VPC',
    'ECS': 'ECS',
    'EKS': 'EKS',
    'RDS': 'RDS',
    'SNS': 'SNS',
    'SQS': 'SQS',
    'KMS': 'KMS',
    'WAF': 'WAF',
    'ACM': 'ACM',
    'EMR': 'EMR',
    'SSM': 'SSM',
    'ELB': 'ELB',
    'ALB': 'ALB',
    'NLB': 'NLB',
    'API': 'API',
    'AWS': 'AWS',
  };

  // Check if type matches <ALLCAPS>Error or <ALLCAPS>Something pattern
  const allCapsMatch = type.match(/^([A-Z0-9]+)(Error|Warning|Alert|Issue|Anomaly|Change|Modification|Access|Violation)?$/);
  if (allCapsMatch) {
    const servicePart = allCapsMatch[1];
    const suffixPart = allCapsMatch[2] || '';
    
    // Look up the service name
    const formattedService = serviceAbbreviations[servicePart] || 
      // Default: convert to title case with spaces between words
      servicePart.charAt(0) + servicePart.slice(1).toLowerCase();
    
    if (suffixPart) {
      return `${formattedService} ${suffixPart}`;
    }
    return formattedService;
  }

  // Handle PascalCase/camelCase by inserting spaces before capital letters
  // But keep abbreviations like S3, EC2, IAM together
  let result = type
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2') // Handle transitions like "IAMPolicy" -> "IAM Policy"
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase transitions
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // Handle "HTTPError" -> "HTTP Error"
  
  return result;
}

// Get cloud incident type label
function getCloudIncidentTypeLabel(type: string): string {
  switch (type) {
    // IAM incidents
    case 'IAMCompromise':
      return 'IAM Compromise';
    case 'IAMPolicyViolation':
      return 'IAM Policy Violation';
    case 'IAMRoleAbuse':
      return 'IAM Role Abuse';
    case 'UnauthorizedAccess':
      return 'Unauthorized Access';
    case 'CredentialLeak':
      return 'Credential Leak';
    case 'PrivilegeEscalation':
      return 'Privilege Escalation';
    // S3 incidents
    case 'S3DataExfiltration':
      return 'S3 Data Exfiltration';
    case 'S3PolicyModification':
      return 'S3 Policy Change';
    case 'S3PublicAccess':
      return 'S3 Public Access';
    case 'S3EncryptionDisabled':
      return 'S3 Encryption Disabled';
    // EC2 incidents
    case 'EC2Anomaly':
      return 'EC2 Anomaly';
    case 'EC2InstanceCompromise':
      return 'EC2 Instance Compromise';
    case 'EC2UnauthorizedAccess':
      return 'EC2 Unauthorized Access';
    // Network incidents
    case 'NetworkTrafficAnomaly':
      return 'Network Traffic Anomaly';
    case 'SecurityGroupModification':
      return 'Security Group Change';
    case 'VPCModification':
      return 'VPC Modification';
    case 'NetworkTrafficBlocked':
      return 'Traffic Blocked';
    // VPC Route incidents
    case 'VPCRouteCreation':
      return 'VPC Route Created';
    case 'VPCRouteDeletion':
      return 'VPC Route Deleted';
    case 'VPCRouteModification':
      return 'VPC Route Modified';
    case 'VPCRouteChange':
      return 'VPC Route Change';
    case 'VPCRouteTableChange':
      return 'VPC Route Table Change';
    case 'VPCDeletion':
      return 'VPC Deletion';
    case 'VPCFlowLogsDisabled':
      return 'VPC Flow Logs Disabled';
    case 'VPCPeeringChange':
      return 'VPC Peering Change';
    case 'VPCGatewayChange':
      return 'VPC Gateway Change';
    case 'VPCSubnetChange':
      return 'VPC Subnet Change';
    // Service health
    case 'ServiceHealthDegradation':
      return 'Service Degradation';
    case 'APIThrottling':
      return 'API Throttling';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    // Config compliance
    case 'ConfigComplianceViolation':
      return 'Config Violation';
    case 'SecurityHubFinding':
      return 'Security Finding';
    // OCI IAM incidents
    case 'OCIIAMCompromise':
      return 'OCI IAM Compromise';
    case 'OCIIAMUserChange':
      return 'OCI IAM User Change';
    case 'OCIIAMGroupChange':
      return 'OCI IAM Group Change';
    case 'OCIDynamicGroupChange':
      return 'OCI Dynamic Group Change';
    case 'OCIPolicyChange':
      return 'OCI Policy Change';
    case 'OCICompartmentChange':
      return 'OCI Compartment Change';
    case 'OCICredentialChange':
      return 'OCI Credential Change';
    case 'OCIPrivilegeEscalation':
      return 'OCI Privilege Escalation';
    case 'OCIIAMSecurityChange':
      return 'OCI IAM Security Change';
    // OCI Cloud Guard
    case 'OCICloudGuardProblem':
      return 'OCI Cloud Guard Problem';
    // OCI Network incidents
    case 'OCISecurityListChange':
      return 'OCI Security List Change';
    case 'OCINetworkSecurityGroupChange':
      return 'OCI Network Security Group Change';
    case 'OCIInternetGatewayChange':
      return 'OCI Internet Gateway Change';
    case 'OCINatGatewayChange':
      return 'OCI NAT Gateway Change';
    case 'OCIServiceGatewayChange':
      return 'OCI Service Gateway Change';
    case 'OCIVCNChange':
      return 'OCI VCN Change';
    case 'OCISubnetChange':
      return 'OCI Subnet Change';
    case 'OCIRouteTableChange':
      return 'OCI Route Table Change';
    case 'OCIDrgChange':
      return 'OCI DRG Change';
    case 'OCIDrgAttachmentChange':
      return 'OCI DRG Attachment Change';
    case 'OCILocalPeeringGatewayChange':
      return 'OCI Local Peering Gateway Change';
    case 'OCIRemotePeeringConnectionChange':
      return 'OCI Remote Peering Connection Change';
    case 'OCIPublicIpChange':
      return 'OCI Public IP Change';
    case 'OCIPrivateIpChange':
      return 'OCI Private IP Change';
    case 'OCIVnicChange':
      return 'OCI VNIC Change';
    case 'OCIDhcpOptionsChange':
      return 'OCI DHCP Options Change';
    case 'OCILoadBalancerChange':
      return 'OCI Load Balancer Change';
    case 'OCINetworkLoadBalancerChange':
      return 'OCI Network Load Balancer Change';
    case 'OCIWAFChange':
      return 'OCI WAF Change';
    case 'OCIDNSChange':
      return 'OCI DNS Change';
    case 'OCIAPIGatewayChange':
      return 'OCI API Gateway Change';
    case 'OCINetworkSecurityChange':
      return 'OCI Network Security Change';
    // OCI Object Storage
    case 'OCIBucketPublicAccess':
      return 'OCI Bucket Public Access';
    case 'OCIBucketPolicyChange':
      return 'OCI Bucket Policy Change';
    case 'OCIObjectStorageChange':
      return 'OCI Object Storage Change';
    // OCI Compute
    case 'OCIComputeAnomaly':
      return 'OCI Compute Anomaly';
    case 'OCIConsoleConnection':
      return 'OCI Console Connection';
    // OCI Database
    case 'OCIDatabaseSecurityChange':
      return 'OCI Database Security Change';
    // OCI KMS/Secrets
    case 'OCIKMSKeyCompromise':
      return 'OCI KMS Key Compromise';
    case 'OCIKMSSecurityChange':
      return 'OCI KMS Security Change';
    // OCI Logging
    case 'OCILoggingTampering':
      return 'OCI Logging Tampering';
    // OCI Generic
    case 'OCIError':
      return 'OCI Error';
    default:
      // Use the smart formatter for unrecognized types
      return formatIncidentType(type);
  }
}

// Get AWS service icon
function getAWSServiceIcon(serviceName: string) {
  switch (serviceName?.toLowerCase()) {
    case 'iam':
      return <Key className="h-4 w-4 text-amber-500" />;
    case 's3':
      return <Database className="h-4 w-4 text-green-500" />;
    case 'ec2':
      return <Server className="h-4 w-4 text-orange-500" />;
    case 'vpc':
    case 'securitygroup':
      return <Network className="h-4 w-4 text-purple-500" />;
    case 'cloudtrail':
      return <Activity className="h-4 w-4 text-blue-500" />;
    default:
      return <Cloud className="h-4 w-4 text-sky-500" />;
  }
}

// Get OCI service icon
function getOCIServiceIcon(serviceName: string) {
  switch (serviceName?.toLowerCase()) {
    case 'identity':
    case 'iam':
      return <Key className="h-4 w-4 text-amber-500" />;
    case 'objectstorage':
      return <Database className="h-4 w-4 text-green-500" />;
    case 'compute':
      return <Server className="h-4 w-4 text-orange-500" />;
    case 'networking':
    case 'vcn':
      return <Network className="h-4 w-4 text-purple-500" />;
    case 'audit':
      return <Activity className="h-4 w-4 text-blue-500" />;
    case 'cloudguard':
      return <Shield className="h-4 w-4 text-red-500" />;
    case 'keymanagement':
    case 'vault':
      return <Key className="h-4 w-4 text-cyan-500" />;
    case 'database':
      return <Database className="h-4 w-4 text-indigo-500" />;
    default:
      return <Cloud className="h-4 w-4 text-orange-400" />;
  }
}

// Get cloud provider icon
function getCloudProviderIcon(cloudProvider?: string) {
  if (cloudProvider === 'oci') {
    return <Cloud className="h-4 w-4 text-orange-500" aria-label="Oracle Cloud" />;
  }
  return <Cloud className="h-4 w-4 text-sky-500" aria-label="AWS" />;
}

// Get cloud service icon based on provider
function getCloudServiceIcon(serviceName: string, cloudProvider?: string) {
  if (cloudProvider === 'oci') {
    return getOCIServiceIcon(serviceName);
  }
  return getAWSServiceIcon(serviceName);
}

export default function CloudIncidentResponse() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<CloudIncidentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [awsConnections, setAWSConnections] = useState<AWSConnection[]>([]);
  const [ociConnections, setOCIConnections] = useState<OCIConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('resolved');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(''); // Debounced search for server-side search
  const [hideTransient, setHideTransient] = useState<boolean>(true);

  // Debounce search query to avoid too many API calls while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 800); // 800ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Date range filtering - default to last 24 hours
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => {
    const lastDay = new Date();
    lastDay.setDate(lastDay.getDate() - 1);
    lastDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return { from: lastDay, to: today };
  });
  
  const [pageSize] = useState<number>(10);
  const [pageToken, setPageToken] = useState<string>('');
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageTokenHistory, setPageTokenHistory] = useState<string[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    investigating: 0,
    analysisComplete: 0,
    resolved: 0,
  });

  // Fetch AWS and OCI connections for filter dropdown
  // Only show AWS connections with the Kestrel Cloud Ingestion role (which are added via "Add Cloud Inventory Connection")
  // All OCI connections are included as they all support incident detection
  useEffect(() => {
    async function fetchCloudConnections() {
      // Fetch AWS connections
      try {
        const response = await apiClient.getAWSConnectionStatus();
        // Filter to only show connections with the Kestrel Cloud Ingestion role
        const cloudIngestionConnections = (response.connections || []).filter(conn => 
          conn.role_arn?.includes('Kestrel-CloudIngestion') || 
          conn.role_arn?.includes('CloudIngestionRole')
        );
        setAWSConnections(cloudIngestionConnections);
      } catch (err: any) {
        console.error('Failed to fetch AWS connections:', err);
      }

      // Fetch OCI connections
      try {
        const response = await apiClient.getOCIConnectionStatus();
        // All OCI connections support incident detection
        setOCIConnections(response.connections || []);
      } catch (err: any) {
        console.error('Failed to fetch OCI connections:', err);
      }
    }

    fetchCloudConnections();
  }, []);

  // Fetch cloud incidents
  useEffect(() => {
    async function fetchIncidents(showLoading = true) {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const startTime = Math.floor(dateRange.from.getTime() / 1000);
        const endTime = Math.floor(dateRange.to.getTime() / 1000);
        
        const response = await apiClient.listCloudIncidents(
          selectedConnection === 'all' ? undefined : selectedConnection,
          undefined, // awsAccountId
          selectedStatus === 'all' ? undefined : selectedStatus,
          pageSize,
          pageToken,
          startTime,
          endTime,
          hideTransient,
          debouncedSearchQuery || undefined  // Server-side search across all incidents
        );
        
        setIncidents(response.incidents || []);
        setTotalCount(response.total_count || 0);
        setNextPageToken(response.next_page_token || '');
        
        // Calculate stats from incidents
        const incidentList = response.incidents || [];
        setStats({
          total: response.total_count || 0,
          investigating: incidentList.filter((i) => i.status === 'investigating' || i.status === 'open').length,
          analysisComplete: incidentList.filter((i) => i.status === 'resolved' && i.rca_result).length,
          resolved: incidentList.filter((i) => i.status === 'remediation_applied').length,
        });
      } catch (err: any) {
        console.error('Failed to fetch cloud incidents:', err);
        if (showLoading) {
          setError(err.response?.data?.message || 'Failed to load cloud incidents');
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    }

    fetchIncidents();

    // Auto-refresh every 30 seconds without showing loading spinner
    const interval = setInterval(() => {
      fetchIncidents(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedConnection, selectedStatus, pageToken, pageSize, dateRange, hideTransient, debouncedSearchQuery]);

  // Reset to first page when filters change (including search)
  useEffect(() => {
    setPageToken('');
    setCurrentPage(1);
    setPageTokenHistory([]);
  }, [selectedConnection, selectedStatus, dateRange, hideTransient, debouncedSearchQuery]);

  const handleNextPage = () => {
    if (nextPageToken) {
      setPageTokenHistory((prev) => [...prev, pageToken]);
      setPageToken(nextPageToken);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && pageTokenHistory.length > 0) {
      const newHistory = [...pageTokenHistory];
      const previousPageToken = newHistory.pop() || '';
      setPageTokenHistory(newHistory);
      setPageToken(previousPageToken);
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Server-side search is now used - incidents are already filtered by the API
  // We just use the incidents directly from the server response
  const paginatedIncidents = incidents;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Get primary resource display
  const getPrimaryResource = (incident: CloudIncidentSummary): string => {
    if (incident.affected_resources && incident.affected_resources.length > 0) {
      const resource = incident.affected_resources[0];
      return resource.resource_id || resource.arn?.split('/').pop() || resource.arn || 'Unknown';
    }
    if (incident.signals && incident.signals.length > 0) {
      return incident.signals[0].resource_id || incident.signals[0].service_name || 'Unknown';
    }
    return 'Unknown';
  };

  // Normalize CloudWatch namespace to friendly service name
  const normalizeServiceName = (serviceName: string): string => {
    const namespaceMap: Record<string, string> = {
      'AWS/Kafka': 'MSK',
      'AWS/MSK': 'MSK',
      'AWS/EC2': 'EC2',
      'AWS/RDS': 'RDS',
      'AWS/Lambda': 'Lambda',
      'AWS/S3': 'S3',
      'AWS/DynamoDB': 'DynamoDB',
      'AWS/ECS': 'ECS',
      'AWS/EKS': 'EKS',
      'AWS/ElastiCache': 'ElastiCache',
      'AWS/CloudFront': 'CloudFront',
      'AWS/SQS': 'SQS',
      'AWS/SNS': 'SNS',
      'AWS/Kinesis': 'Kinesis',
    };
    return namespaceMap[serviceName] || serviceName;
  };

  // Get primary service display
  const getPrimaryService = (incident: CloudIncidentSummary): string => {
    if (incident.signals && incident.signals.length > 0) {
      const rawService = incident.signals[0].service_name || 'AWS';
      return normalizeServiceName(rawService);
    }
    if (incident.affected_resources && incident.affected_resources.length > 0) {
      const resourceType = incident.affected_resources[0].resource_type;
      if (resourceType) {
        const parts = resourceType.split('::');
        return parts.length >= 2 ? parts[1] : resourceType;
      }
    }
    return 'AWS';
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Incidents</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Investigating</CardTitle>
            <Loader2 className={`h-4 w-4 text-muted-foreground ${stats.investigating > 0 ? 'animate-spin' : ''}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.investigating}</div>
            <p className="text-xs text-muted-foreground">
              AI analysis in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Analysis Complete</CardTitle>
            <FileWarning className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.analysisComplete}</div>
            <p className="text-xs text-muted-foreground">
              Ready to apply
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.resolved}</div>
            <p className="text-xs text-muted-foreground">
              Incident closed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search cloud incidents by type, account, or resource..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[280px] justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange?.from && dateRange?.to ? (
                    <>
                      {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                    </>
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={{ from: dateRange?.from, to: dateRange?.to }}
                  onSelect={(range: any) => {
                    if (range?.from && range?.to) {
                      const from = new Date(range.from);
                      from.setHours(0, 0, 0, 0);
                      const to = new Date(range.to);
                      to.setHours(23, 59, 59, 999);
                      setDateRange({ from, to });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {/* Cloud Account Filter */}
            <Select value={selectedConnection} onValueChange={setSelectedConnection}>
              <SelectTrigger className="w-[220px]">
                <Cloud className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Cloud Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {awsConnections.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">AWS</div>
                    {awsConnections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.account_id}
                      </SelectItem>
                    ))}
                  </>
                )}
                {ociConnections.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">OCI</div>
                    {ociConnections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.connection_name || conn.tenancy_ocid.split('.').pop()?.substring(0, 12) || 'OCI Tenancy'}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[200px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="resolved">Analysis Complete</SelectItem>
                <SelectItem value="remediation_applied">Resolved</SelectItem>
              </SelectContent>
            </Select>

            {/* Hide Transient Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={hideTransient ? "secondary" : "outline"}
                    onClick={() => setHideTransient(!hideTransient)}
                    className="whitespace-nowrap"
                  >
                    {hideTransient ? (
                      <XCircle className="mr-2 h-4 w-4" />
                    ) : (
                      <Activity className="mr-2 h-4 w-4" />
                    )}
                    {hideTransient ? 'Hiding Transient' : 'Show All'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{hideTransient ? 'Click to show transient incidents' : 'Click to hide transient incidents'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Incidents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detected Incidents</CardTitle>
          <CardDescription>
            Cloud incidents detected and analyzed by AI agents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : paginatedIncidents.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium">No cloud incidents found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {searchQuery || dateRange
                  ? 'No incidents match your search criteria. Try adjusting your filters or date range.'
                  : 'All systems are operating normally. Incidents will appear here when detected.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Incident</TableHead>
                    <TableHead>Cloud Account</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Detected</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedIncidents.map((incident) => (
                    <TableRow 
                      key={incident.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/cloud-incidents/${incident.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{getCloudIncidentTypeLabel(incident.type)}</span>
                          {(incident.source_type === 'pagerduty' || incident.has_pagerduty_signals) && (
                            <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              PagerDuty
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Badge variant={incident.severity === "critical" || incident.severity === "high" ? "destructive" : incident.severity === "medium" ? "default" : "secondary"} className="text-xs">{incident.severity}</Badge>
                          {incident.rca_result?.transient && (
                            <Badge variant="outline" className="text-xs">Transient</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1 py-0.5 rounded">
                          {incident.cloud_provider === 'oci' 
                            ? (ociConnections.find(c => c.id === incident.oci_connection_id)?.connection_name 
                               || incident.oci_tenancy_ocid?.split('.').pop()?.substring(0, 12) 
                               || 'OCI')
                            : incident.aws_account_id}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {incident.cloud_provider === "oci" ? <img src={OCILogo} alt="OCI" className="h-4 w-4" /> : <><img src={awsLogoLight} alt="AWS" className="h-4 w-4 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-4 w-4 hidden dark:block" /></>}
                          <span className="text-sm">{getPrimaryService(incident)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono truncate max-w-[200px]">
                            {getPrimaryResource(incident)}
                          </span>
                          {incident.cloud_resource_owner_emails && incident.cloud_resource_owner_emails.length > 0 && (
                            <TooltipProvider>
                              <div className="flex items-center -space-x-2">
                                {incident.cloud_resource_owner_emails.slice(0, 2).map((email, idx) => (
                                  <Tooltip key={email}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className="relative w-6 h-6 rounded-full flex items-center justify-center text-white font-semibold text-xs bg-gradient-to-br from-purple-500 to-purple-600 border-2 border-background shadow-sm cursor-help"
                                        style={{ zIndex: incident.cloud_resource_owner_emails!.length - idx }}
                                      >
                                        {getInitials(email)}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">Resource Owner: {email}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                                {incident.cloud_resource_owner_emails.length > 2 && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="relative w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground font-semibold text-xs bg-muted border-2 border-background shadow-sm cursor-help">
                                        +{incident.cloud_resource_owner_emails.length - 2}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {incident.cloud_resource_owner_emails.slice(2).map(email => (
                                        <p key={email} className="text-xs">Resource Owner: {email}</p>
                                      ))}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(incident.status)}>
                          {getStatusLabel(incident.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="text-sm text-muted-foreground">
                                {formatTimeAgo(incident.detected_at)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{formatDateTime(incident.detected_at)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/cloud-incidents/${incident.id}`);
                        }}>
                          View Details <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} incidents
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

